import { formatFactorRangeDisplayLabel } from '@/utils/starRangeBounds'

/** 口令/已选因子展示：对比 descript 与实际选项，有差异时用选项文案 */

export function normalizeFactorLabelText(s: string): string {
  return String(s || '').replace(/\s+/g, '').replace(/[：:]/g, '')
}

/** 是否为因子级汇总 descript（非单个子项名称） */
export function isCombinedFactorDescript(name: string, factorDescript?: string): boolean {
  const n = String(name || '').trim()
  if (!n) return false
  const fd = factorDescript ? String(factorDescript).trim() : ''
  if (fd && normalizeFactorLabelText(n) === normalizeFactorLabelText(fd)) return true
  return /[或且]/.test(n)
}

export function getOptionsJoinSep(item: any): string {
  return item?.unionMode === 'intersect' ? ' 且 ' : ' 或 '
}

function dedupeSelectedOptions(opts: any[]): any[] {
  const seen = new Set<string>()
  return opts.filter((o) => {
    const key = String(o?.attrCode ?? o?.attrName ?? '')
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** 用户修改因子后去掉口令 descript，避免与 selectedOptions 重复展示 */
export function stripWatchwordSelectionDescript(factor: any): any {
  if (!factor || factor.selectionDescript == null) return factor
  const { selectionDescript: _d, ...rest } = factor
  return { ...rest, _userModified: true }
}

/** 用因子树子节点纠正子项名称（避免口令把整条 descript 挂在每个 option 上） */
export function normalizeSelectedOptionsFromTree(
  selectedOptions: any[],
  childAttrList: any[] | undefined,
): any[] {
  if (!selectedOptions?.length || !childAttrList?.length) return selectedOptions || []
  const byCode = new Map(childAttrList.map((o: any) => [String(o.attrCode), o]))
  return selectedOptions.map((p) => {
    const node = byCode.get(String(p.attrCode))
    if (node?.attrName) {
      return { attrCode: p.attrCode, attrName: node.attrName }
    }
    return p
  })
}

export function buildFactorOptionsText(item: any): string {
  const hint = getWatchwordDescript(item)
  let opts = dedupeSelectedOptions(item?.selectedOptions || [])
  // 主题（attrType=6）子项名常含「与」，勿按组合 descript 规则重写
  if (
    item?.attrType !== 6 &&
    opts.length > 1 &&
    opts.every((o) => isCombinedFactorDescript(o.attrName, hint))
  ) {
    opts = normalizeSelectedOptionsFromTree(opts, item.childAttrList)
  }
  if (!opts.length) return ''
  const sep = getOptionsJoinSep(item)
  switch (item.attrType) {
    case 1:
    case 2:
      return opts[0]?.attrName || ''
    case 3:
    case 5:
    case 4:
    case 6:
      return opts.map((o: any) => o.attrName).join(sep)
    default:
      return opts.map((o: any) => o.attrName).join(sep)
  }
}

export function isCodeLikeFactorLabel(name: string, code?: string): boolean {
  const n = String(name || '').trim()
  const c = String(code || '').trim()
  if (!n) return true
  if (c && n === c) return true
  return /^[a-z][a-z0-9_]*$/i.test(n)
}

/** 口令默认回显：未手动改过时，列表/卡片只展示接口 descript */
export function shouldUseWatchwordDescriptOnly(item: any): boolean {
  if (item?._userModified) return false
  const hint = item?.selectionDescript && String(item.selectionDescript).trim()
  return !!hint
}

export function getWatchwordDescript(item: any): string {
  return item?.selectionDescript ? String(item.selectionDescript).trim() : ''
}

/** 列表展示：因子名 + 接口 descript（descript 通常不含因子名，如「大于30.00亿」） */
export function joinFactorNameAndHint(name: string, hint: string): string {
  const n = String(name || '').trim()
  const h = String(hint || '').trim()
  if (!h) return n
  if (!n) return h
  if (h.startsWith(`${n}：`) || h.startsWith(`${n}:`)) return h
  const nNorm = normalizeFactorLabelText(n)
  const hNorm = normalizeFactorLabelText(h)
  if (hNorm.startsWith(nNorm) && /[：:]/.test(h)) return h
  return `${n}：${h}`
}

/** 是否应以 selectedOptions 为准（仅用户修改后与 descript 不一致时） */
export function shouldPreferOptionsOverHint(item: any): boolean {
  if (shouldUseWatchwordDescriptOnly(item)) return false
  const opts = dedupeSelectedOptions(item?.selectedOptions || [])
  if (!opts.length) return false
  if (item._userModified) return true
  const hint = getWatchwordDescript(item)
  const optionsText = buildFactorOptionsText(item)
  if (!optionsText) return false
  if (!hint) return true
  const optsAreCodes = opts.every((o: any) => isCodeLikeFactorLabel(o.attrName, o.attrCode))
  if (optsAreCodes) return false
  return normalizeFactorLabelText(hint) !== normalizeFactorLabelText(optionsText)
}

export function resolveFactorSubLabelText(item: any): string {
  if (item?._confirmed && item?.selectedOptions?.length) {
    const direct = buildFactorOptionsText(item)
    if (direct) return direct
  }
  const hint = getWatchwordDescript(item)
  if (item?.attrType === 7) {
    if (shouldUseWatchwordDescriptOnly(item) && hint) return hint
    const rangeText = formatFactorRangeDisplayLabel(
      item.attrName || '',
      item.selectedRange,
      item.attrUnit || item.unit || '',
    )
    if (rangeText) return rangeText
  }
  if (shouldUseWatchwordDescriptOnly(item) && hint) return hint
  const optionsText = buildFactorOptionsText(item)
  if (optionsText && shouldPreferOptionsOverHint(item)) return optionsText
  return hint || optionsText || ''
}

/** 左侧已选列表：因子名 + 子项描述 */
export function formatFactorSelectedListLabel(item: any): string {
  const name = item?.attrName || ''
  const hint = getWatchwordDescript(item)
  const opts: any[] = item?.selectedOptions || []

  if (item.attrType === 7 && shouldUseWatchwordDescriptOnly(item) && hint) {
    return joinFactorNameAndHint(name, hint)
  }

  if (shouldUseWatchwordDescriptOnly(item) && hint) {
    if (item.attrType === 1 || (opts.length === 0 && item.attrType !== 7)) {
      return `${name}（${hint}）`
    }
    return joinFactorNameAndHint(name, hint)
  }

  const optionsText = buildFactorOptionsText(item)
  if (item.attrType === 7) {
    const range = item.selectedRange
    const rangeText = formatFactorRangeDisplayLabel(
      name,
      range,
      item.attrUnit || item.unit || '',
    )
    if (rangeText) return rangeText
    if (hint) return `${name}（${hint}）`
    return name
  }
  if (optionsText && shouldPreferOptionsOverHint(item)) {
    if (item.attrType === 1) return `${name}（${optionsText}）`
    return `${name}：${optionsText}`
  }
  if (hint) {
    if (opts.length === 0 && item.attrType !== 7) return `${name}（${hint}）`
    return joinFactorNameAndHint(name, hint)
  }
  if (optionsText) {
    if (item.attrType === 1) return `${name}（${optionsText}）`
    return `${name}：${optionsText}`
  }
  return name
}
